/**
 * AuthShell — modern split-screen layout for all auth pages.
 * @file src/web/features/auth/components/AuthShell.tsx
 *
 * Replaces uikit's stock AuthLayout so login/register/password pages
 * match the oddits landing aesthetic (indigo→violet, emerald "valid").
 * Left: the form. Right (lg+): a branded panel with the product story.
 *
 * Also exports shared primitives so every auth page styles inputs,
 * buttons and alerts identically: `authInputClass`, `authLabelClass`,
 * <AuthButton>, <AuthAlert>.
 */

import { Link } from 'react-router-dom';
import { ArrowLeft, CheckCircle2, Scale, ShieldCheck, Loader2, AlertTriangle } from 'lucide-react';
import { brand } from '../../../shared/brand';

export const authInputClass =
  'w-full rounded-xl border border-receipt bg-paper pl-10 pr-4 py-2.5 text-sm text-inkblack placeholder:text-inkblack-60 shadow-sm transition focus:border-inkblack focus:outline-none focus:ring-2 focus:ring-highlighter';

export const authLabelClass = 'text-sm font-medium text-inkblack-80';

export function AuthButton({
  children,
  loading,
  ...props
}: React.ButtonHTMLAttributes<HTMLButtonElement> & { loading?: boolean }) {
  return (
    <button
      {...props}
      disabled={props.disabled || loading}
      className="group inline-flex w-full items-center justify-center gap-2 rounded-xl bg-inkblack px-4 py-2.5 text-sm font-semibold text-paper shadow-sm transition-all hover:-translate-y-0.5 hover:bg-inkblack-80 hover:shadow-md disabled:cursor-not-allowed disabled:opacity-60 disabled:hover:translate-y-0"
    >
      {loading && <Loader2 className="h-4 w-4 animate-spin" />}
      {children}
    </button>
  );
}

export function AuthAlert({
  tone = 'error',
  title,
  children,
  onClose,
}: {
  tone?: 'error' | 'success';
  title: string;
  children?: React.ReactNode;
  onClose?: () => void;
}) {
  const styles =
    tone === 'success'
      ? 'border-ok-200 bg-ok-50 text-ok-800'
      : 'border-danger-200 bg-danger-50 text-danger-800';
  const Icon = tone === 'success' ? CheckCircle2 : AlertTriangle;
  return (
    <div className={`relative rounded-xl border px-4 py-3 text-sm ${styles}`}>
      <div className="flex gap-2.5">
        <Icon className="mt-0.5 h-4 w-4 shrink-0" />
        <div className="pr-5">
          <p className="font-semibold">{title}</p>
          {children && <div className="mt-0.5 opacity-90">{children}</div>}
        </div>
      </div>
      {onClose && (
        <button onClick={onClose} className="absolute right-2.5 top-2.5 opacity-60 hover:opacity-100" aria-label="Dismiss">
          ×
        </button>
      )}
    </div>
  );
}

const BRAND_POINTS = [
  { icon: Scale, text: 'Books that reconcile — debits equal credits, every time.' },
  { icon: CheckCircle2, text: 'One-click Tally import, validated before you look.' },
  { icon: ShieldCheck, text: 'Role-based access and a full audit trail.' },
];

export function AuthShell({
  title,
  subtitle,
  children,
  footer,
}: {
  title: string;
  subtitle?: string;
  children: React.ReactNode;
  footer?: React.ReactNode;
}) {
  return (
    <div className="flex min-h-screen bg-paper text-inkblack antialiased">
      {/* form side */}
      <div className="flex w-full flex-col px-6 py-8 lg:w-[46%] lg:px-14">
        <div className="flex items-center justify-between">
          <Link to="/" className="no-underline">
            <img src={brand.logo} alt="oddits" className="h-9 w-auto" />
          </Link>
          <Link to="/" className="inline-flex items-center gap-1.5 text-sm font-medium text-inkblack-60 no-underline transition-colors hover:text-inkblack">
            <ArrowLeft className="h-4 w-4" /> Home
          </Link>
        </div>

        <div className="flex flex-1 items-center">
          <div className="mx-auto w-full max-w-md py-12">
            <h1 className="text-2xl font-bold tracking-tight text-inkblack sm:text-3xl">{title}</h1>
            {subtitle && <p className="mt-2 text-sm text-inkblack-60">{subtitle}</p>}
            <div className="mt-8">{children}</div>
            {footer && <div className="mt-8 text-center text-sm text-inkblack-60">{footer}</div>}
          </div>
        </div>
      </div>

      {/* brand side */}
      <div className="relative hidden overflow-hidden bg-inkblack lg:flex lg:w-[54%]">
        <div aria-hidden className="pointer-events-none absolute inset-0 opacity-20 [background-image:radial-gradient(circle,#FFE77D_1px,transparent_1px)] [background-size:26px_26px]" />
        <div aria-hidden className="pointer-events-none absolute -right-24 -top-24 h-96 w-96 rounded-full bg-highlighter/15 blur-3xl" />

        <div className="relative z-10 flex flex-col justify-center px-14 py-16 text-paper">
          <span className="inline-flex w-fit items-center gap-1.5 rounded-full border border-highlighter/40 bg-highlighter/10 px-3 py-1 text-xs font-medium text-highlighter">
            Practice management for CAs
          </span>
          <h2 className="mt-6 max-w-md text-3xl font-semibold leading-tight xl:text-4xl">
            Your whole practice, in one calm place.
          </h2>
          <p className="mt-4 max-w-md text-receipt">
            Clients, books, compliance and deadlines together — with Tally data
            that&rsquo;s imported and reconciled before you ever look.
          </p>

          <ul className="mt-8 space-y-4">
            {BRAND_POINTS.map((p) => (
              <li key={p.text} className="flex items-start gap-3">
                <span className="mt-0.5 grid size-6 shrink-0 place-items-center rounded-full bg-highlighter/20 text-highlighter">
                  <p.icon className="h-3.5 w-3.5" />
                </span>
                <span className="text-sm text-receipt">{p.text}</span>
              </li>
            ))}
          </ul>

          {/* mini validated card */}
          <div className="mt-10 w-fit rounded-2xl border border-highlighter/20 bg-paper/5 p-4 backdrop-blur">
            <div className="flex items-center gap-3">
              <span className="grid size-9 place-items-center rounded-lg bg-ok-400/20">
                <CheckCircle2 className="h-4.5 w-4.5 text-ok-200" />
              </span>
              <div>
                <p className="text-sm font-semibold">Day Book · Validated</p>
                <p className="text-xs text-receipt">360 vouchers · debits = credits</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
